import { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { isEqual } from "lodash"

import TextField from "@/components/form/textField/TextField"
import { useCalculateUnitQuery, useUnitQuery } from "@/hooks/api/useSettingQuery"
import { Jump, ReadJumpActionResponse } from "@/types/setting"
import { ActionSettingType, UnitType } from "@/constants/unit"
import {
  DEFAULT_MIN_JUMP_HEIGHT,
  DEFAULT_MAX_JUMP_HEIGHT,
  DEFAULT_LOW_JUMP_HEIGHT,
  DEFAULT_MEDIUM_JUMP_HEIGHT,
  DEFAULT_HIGH_JUMP_HEIGHT,
} from "@/app/settings/metric/actions/constants"
import { createActionSettingState } from "@/stores/settings"

export default function JumpActionTab({
  initialData,
  editedData,
  setEditedData,
}: {
  initialData: ReadJumpActionResponse | undefined
  editedData: ReadJumpActionResponse | null
  setEditedData: (state: ReadJumpActionResponse) => void
}) {
  const { t } = useTranslation()
  const { setIsAllValid, setHasChanges } = createActionSettingState()

  const { data: unit } = useUnitQuery()
  const { data: calcMin } = useCalculateUnitQuery({
    type: UnitType.JUMP_DISTANCE,
    value: DEFAULT_MIN_JUMP_HEIGHT,
  })
  const { data: calcMax } = useCalculateUnitQuery({
    type: UnitType.JUMP_DISTANCE,
    value: DEFAULT_MAX_JUMP_HEIGHT,
  })

  const [jumpValue, setJumpValue] = useState<Jump>({
    lowJumpHeight: DEFAULT_LOW_JUMP_HEIGHT,
    mediumJumpHeight: DEFAULT_MEDIUM_JUMP_HEIGHT,
    highJumpHeight: DEFAULT_HIGH_JUMP_HEIGHT,
    minJumpHeight: calcMin?.result || DEFAULT_MIN_JUMP_HEIGHT,
    maxJumpHeight: calcMax?.result || DEFAULT_MAX_JUMP_HEIGHT,
  })
  const [lastTouched, setLastTouched] = useState<"low" | "medium" | "high" | undefined>()

  useEffect(() => {
    const newValue = editedData?.global || initialData?.global
    if (newValue) {
      setJumpValue((prev) => {
        if (isEqual(prev, newValue)) {
          return prev
        }

        return newValue
      })
    }
  }, [initialData, editedData])

  useEffect(() => {
    if (!initialData) {
      return
    }

    const hasChanged = !isEqual(initialData.global, jumpValue)
    setHasChanges(ActionSettingType.JUMP, hasChanged)
  }, [initialData, jumpValue])

  useEffect(() => {
    setIsAllValid(ActionSettingType.JUMP, !isErr())
  }, [jumpValue])

  const isErrThreshold = (type: "low" | "medium" | "high", value: number) => {
    const isUnderMin = value < (calcMin?.result || DEFAULT_MIN_JUMP_HEIGHT)
    const isOverMax = value > (calcMax?.result || DEFAULT_MAX_JUMP_HEIGHT)

    switch (type) {
      case "low":
        return isUnderMin || isOverMax || value >= jumpValue.mediumJumpHeight
      case "medium":
        return (
          isUnderMin ||
          isOverMax ||
          value >= jumpValue.highJumpHeight ||
          value <= jumpValue.lowJumpHeight
        )
      case "high":
        return isUnderMin || isOverMax || value <= jumpValue.mediumJumpHeight
      default:
        return false
    }
  }

  const isErr = () => {
    const isEmpty =
      !jumpValue.lowJumpHeight || !jumpValue.mediumJumpHeight || !jumpValue.highJumpHeight

    return (
      isEmpty ||
      isErrThreshold("low", jumpValue.lowJumpHeight) ||
      isErrThreshold("medium", jumpValue.mediumJumpHeight) ||
      isErrThreshold("high", jumpValue.highJumpHeight)
    )
  }

  const getErrMessage = (type: "low" | "medium" | "high", value: number) => {
    const isUnderMin = value < (calcMin?.result || DEFAULT_MIN_JUMP_HEIGHT)
    const isOverMax = value > (calcMax?.result || DEFAULT_MAX_JUMP_HEIGHT)

    if (isUnderMin) {
      return t("rules.min", {
        name: `${t(`${type}JumpHeight`)}`,
        min: calcMin?.result || DEFAULT_MIN_JUMP_HEIGHT,
      })
    }
    if (isOverMax) {
      return t("rules.max", {
        name: `${t(`${type}JumpHeight`)}`,
        max: calcMax?.result || DEFAULT_MAX_JUMP_HEIGHT,
      })
    }

    switch (type) {
      case "low":
        if (jumpValue.lowJumpHeight > jumpValue.mediumJumpHeight) {
          return t("rules.min", {
            name: `${t("metric.lowJumpHeight")}`,
            min: jumpValue.mediumJumpHeight,
          })
        }
        break
      case "medium":
        if (jumpValue.mediumJumpHeight > jumpValue.highJumpHeight) {
          return t("rules.max", {
            name: `${t("metric.mediumJumpHeight")}`,
            max: jumpValue.highJumpHeight,
          })
        }
        if (jumpValue.mediumJumpHeight < jumpValue.lowJumpHeight) {
          return t("rules.min", {
            name: `${t("metric.mediumJumpHeight")}`,
            min: jumpValue.lowJumpHeight,
          })
        }
        break
      case "high":
        if (jumpValue.highJumpHeight < jumpValue.mediumJumpHeight) {
          return t("rules.min", {
            name: `${t("metric.highJumpHeight")}`,
            min: jumpValue.mediumJumpHeight,
          })
        }
        break
      default:
        return ""
    }

    if (jumpValue.lowJumpHeight < DEFAULT_MIN_JUMP_HEIGHT) {
      return t("rules.min", {
        name: `${t("jump")} ${t("threshold")}`,
        min: DEFAULT_MIN_JUMP_HEIGHT,
      })
    } else if (jumpValue.lowJumpHeight > DEFAULT_MAX_JUMP_HEIGHT) {
      return t("rules.max", {
        name: `${t("jump")} ${t("threshold")}`,
        max: DEFAULT_MAX_JUMP_HEIGHT,
      })
    } else {
      return ""
    }
  }

  const handleChange = (field: keyof Jump, value: number) => {
    const newJumpValue = {
      ...jumpValue,
      [field]: value,
    }
    setJumpValue(newJumpValue)
    setEditedData({ global: newJumpValue })
  }

  const handleBlur = (field: keyof Jump, value: number) => {
    const roundedValue = Math.round(value * 100) / 100
    const newJumpValue = {
      ...jumpValue,
      [field]: roundedValue,
    }
    setJumpValue(newJumpValue)
    setEditedData({ global: newJumpValue })
    setLastTouched(undefined)
  }

  return (
    <>
      <div className="ActionRowCont">
        <div className="row-title-editable">
          <p className="row-title-default-label">{`${t(`jump`)} ${t("effort")} 1`}</p>
          <div className="row-title-label">
            <p>{t("metric.lowJumpHeight")}</p>
          </div>
        </div>

        <div className="ActionRowItemCont">
          <div className="action-threshold">
            <p className="action-form-label">
              {t("threshold")} ({unit?.find((un) => un.type === UnitType.JUMP_DISTANCE)?.unit})
            </p>
            <div className="jump-threshold-row">
              <TextField
                type="number"
                slotProps={{
                  htmlInput: {
                    step: 0.01,
                  },
                }}
                value={jumpValue.lowJumpHeight}
                onChange={(e) => {
                  handleChange("lowJumpHeight", Number(e.target.value))
                  if (isErrThreshold("low", Number(e.target.value))) {
                    setLastTouched("low")
                  }
                }}
                onBlur={(e) => handleBlur("lowJumpHeight", Number(e.target.value))}
                onFocusCapture={() => setLastTouched("low")}
                error={isErrThreshold("low", jumpValue.lowJumpHeight)}
              />
              <p>~</p>
              <TextField
                value={jumpValue.mediumJumpHeight}
                slotProps={{
                  input: {
                    readOnly: true,
                  },
                }}
              />
            </div>
            {lastTouched === "low" && isErrThreshold("low", jumpValue.lowJumpHeight) && (
              <p className="threshold-error-message">
                {getErrMessage("low", jumpValue.lowJumpHeight)}
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="ActionRowCont">
        <div className="row-title-editable">
          <p className="row-title-default-label">{`${t(`jump`)} ${t("effort")} 2`}</p>
          <div className="row-title-label">
            <p>{t("metric.mediumJumpHeight")}</p>
          </div>
        </div>

        <div className="ActionRowItemCont">
          <div className="action-threshold">
            <p className="action-form-label">
              {t("threshold")} ({unit?.find((un) => un.type === UnitType.JUMP_DISTANCE)?.unit})
            </p>
            <div className="jump-threshold-row">
              <TextField
                type="number"
                slotProps={{
                  htmlInput: {
                    step: 0.01,
                  },
                }}
                value={jumpValue.mediumJumpHeight}
                onChange={(e) => {
                  handleChange("mediumJumpHeight", Number(e.target.value))
                  if (isErrThreshold("medium", Number(e.target.value))) {
                    setLastTouched("medium")
                  }
                }}
                onBlur={(e) => handleBlur("mediumJumpHeight", Number(e.target.value))}
                onFocus={() => setLastTouched("medium")}
                error={isErrThreshold("medium", jumpValue.mediumJumpHeight)}
              />
              <p>~</p>
              <TextField
                value={jumpValue.highJumpHeight}
                slotProps={{
                  input: {
                    readOnly: true,
                  },
                }}
              />
            </div>
            {lastTouched === "medium" && isErrThreshold("medium", jumpValue.mediumJumpHeight) && (
              <p className="threshold-error-message">
                {getErrMessage("medium", jumpValue.mediumJumpHeight)}
              </p>
            )}
          </div>
        </div>
      </div>
      <div className="ActionRowCont">
        <div className="row-title-editable">
          <p className="row-title-default-label">{`${t(`jump`)} ${t("effort")} 3`}</p>
          <div className="row-title-label">
            <p>{t("metric.highJumpHeight")}</p>
          </div>
        </div>

        <div className="ActionRowItemCont">
          <div className="action-threshold">
            <p className="action-form-label">
              {t("threshold")} ({unit?.find((un) => un.type === UnitType.JUMP_DISTANCE)?.unit})
            </p>
            <div className="jump-threshold-row">
              <TextField
                type="number"
                value={jumpValue.highJumpHeight}
                onChange={(e) => {
                  handleChange("highJumpHeight", Number(e.target.value))
                  if (isErrThreshold("high", Number(e.target.value))) {
                    setLastTouched("high")
                  }
                }}
                onBlur={(e) => handleBlur("highJumpHeight", Number(e.target.value))}
                onFocus={() => setLastTouched("high")}
                error={isErrThreshold("high", jumpValue.highJumpHeight)}
              />
              <p>~</p>
            </div>
            {lastTouched === "high" && isErrThreshold("high", jumpValue.highJumpHeight) && (
              <p className="threshold-error-message">
                {getErrMessage("high", jumpValue.highJumpHeight)}
              </p>
            )}
          </div>
        </div>
      </div>
    </>
  )
}
